import {stringToDuration} from '@wix/music-shared/dist/src/utils/time/time';
import {EventEmitter} from 'events';
import {injectable, decorate} from 'inversify';
import {getOriginalComponentId} from '../env/compId';
import {isStandalone} from '../env/viewMode/viewMode';
import {isTrackShortPreviewEnabled} from '../utils/trackUtils/trackUtils';
import {IPlayer} from './IPlayer';
import {LocalPlayer} from './localPlayer/localPlayer';
import {PlayerFacade} from './playerFacade/playerFacade';
import {playersVariants, TConverter} from './playerVariants';
import {RemotePlayer} from './remotePlayer/remotePlayer';

/**
 *   This is main public api. Provides methods for playing tracks in album.
 *   Basic methods: play, pause, next, previous.
 *   Events: playing, pause, timeupdate.
 *
 *   Special feature - player supports different 'play implementations'.
 *   It can play with local audio player (created using Audio tag in current window) and
 *   using remote player (sending post events via transports). Switching can be done seamlessly, using player facade.
 */
export class Player extends EventEmitter {
  private inited: boolean;
  private album: any;
  private localConverter: TConverter;
  private converter: TConverter;
  private hasSwitchedPlayer: boolean;
  private playerFacade: PlayerFacade<string>;
  private localPlayer: IPlayer;
  private playerImplementation: IPlayer;
  private __currentTrack: any;

  constructor(album: any, track?: any) {
    super();
    this.inited = false;
    this.album = album;

    const strategy = this._choosePlayer();
    const tracks = album ? album.tracks : [];
    this.localConverter = playersVariants.LOCAL.converter(tracks);
    this.converter = strategy.converter(tracks);
    this.hasSwitchedPlayer = false;

    this.initFacade();
    this.on('timeupdate', ({track}) => {
      if (track !== this.currentTrack) {
        this.currentTrack = track;
      }
    });
    if (track) {
      this.currentTrack = track;
    }
  }

  get paused() {
    return this.playerFacade.paused;
  }

  get currentTime() {
    return this.playerFacade.currentTime;
  }

  get progress() {
    if (!this.currentTrack) {
      return 0;
    }
    return Math.min(this.currentTime / this.duration, 1); // because stored value is not too precise and we can't get value more than 1
  }

  get duration(): number {
    const {currentTrack} = this;
    if (!currentTrack) {
      return 0;
    }
    const duration = stringToDuration(currentTrack.trackDuration);

    return isTrackShortPreviewEnabled(currentTrack) ? Math.min(duration, 30) : duration;
  }

  get currentTrack() {
    return this.__currentTrack;
  }

  set currentTrack(value: any) {
    const oldValue = this.currentTrack;
    if (value) {
      this.__currentTrack = value;
      this.localPlayer.src = this.localConverter.toSrc(value);
      this.playerFacade.src = this.converter.toSrc(value);
      this.playerFacade.currentTrackDuration = stringToDuration(value.trackDuration);

      if (value !== oldValue) {
        this.emit('track-change', {track: this.currentTrack});
      }
    }
  }

  // {outgoingTransport, incomingTransport}
  _createRemotePlayer(options = {}): IPlayer {
    return new RemotePlayer({originalComponentId: getOriginalComponentId(), ...options});
  }

  _createLocalPlayer(): IPlayer {
    return new LocalPlayer();
  }

  initFacade() {
    // TODO: this should be better injected as dependency to class
    this.localPlayer = this._createLocalPlayer();
    this.playerImplementation = isStandalone() ? this.localPlayer : this._createRemotePlayer();

    this.playerFacade = new PlayerFacade({implementation: this.playerImplementation});
    this._subscribeToFacadeEvents();
  }

  init() {
    if (this.inited === true) {
      return Promise.resolve();
    }
    this.localPlayer.init();
    return this.playerFacade.init().then(() => {
      this.inited = true;
      this.currentTrack = this.converter.fromSrc(this.playerFacade.src) || this.album.tracks[0];
      this.emit('init', {track: this.currentTrack, paused: this.paused});
    });
  }

  play() {
    // We need to play/pause on local player
    // so it happens in the context of click so it would work
    // then we call pause() and do all the post messages stuff we need
    // and then in widget when we call ".play()" it would work
    // because we already had called ".play()" in the context of click
    // by that moment.
    this.localPlayer.play();
    this.localPlayer.pause();
    this.init().then(() => {
      if (!this.currentTrack) {
        this.currentTrack = this.album.tracks[0];
      }
      this.playerFacade.play();
    });
  }

  pause() {
    this.init().then(() => {
      this.playerFacade.pause();
    });
  }

  next() {
    return this.init().then(() => {
      const {tracks} = this.album;
      const index = tracks.indexOf(this.currentTrack);
      if (index === -1) {
        throw new Error(`Album doesn't have track`);
      }
      const nextTrackIndex = (index + 1) % tracks.length;
      this.currentTrack = tracks[nextTrackIndex];
    });
  }

  previous() {
    return this.init().then(() => {
      const {tracks} = this.album;
      const index = tracks.indexOf(this.currentTrack);
      if (index === -1) {
        throw new Error(`Album doesn't have track`);
      }
      const previousTrackIndex = (index - 1 + tracks.length) % tracks.length;
      this.currentTrack = tracks[previousTrackIndex];
    });
  }

  updateCurrentTime(value) {
    this.playerFacade.updateCurrentTime(value);
  }

  _choosePlayer() {
    if (isStandalone()) {
      return playersVariants.LOCAL;
    }
    return playersVariants.REMOTE;
  }

  _subscribeToFacadeEvents() {
    this.playerFacade.on('playing', (param) => {
      this.emit('playing', {track: this.converter.fromEvent(param)});
    });
    this.playerFacade.on('pause', (param) => this.emit('pause', {track: this.converter.fromEvent(param)}));
    this.playerFacade.on('error', () => {
      if (this.hasSwitchedPlayer) {
        return;
      }
      this.hasSwitchedPlayer = true;
      this.playerImplementation = this.localPlayer;
      this.playerFacade.setImplementation(this.localPlayer);
      this.converter = this.localConverter;
      this.currentTrack = this.converter.fromSrc(this.playerFacade.src) || this.album.tracks[0];
      this.playerFacade.play();
    });
    this.playerFacade.on('timeupdate', (param) =>
      this.emit('timeupdate', {currentTime: param.currentTime, track: this.converter.fromEvent(param)}),
    );
  }
}

decorate(injectable(), Player);
